import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig } from '@ddt-web/data-service';
import { FundPreferenceStoreService } from '@ddt-web/fund-preferences';
import { StoreEntity, StoreService } from '@ddt-web/store-service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { FUND_TAGS_STORE } from '../constants';
import { FundTagConfig, FundTagGroupConfig } from '../models/fund-tag-config.interface';
import { FundTagData, FundTags } from '../models/fund-tags.interface';

/**
 * In order to ensure that only valid fund tags are added to the Fund Tag store,
 * this service must be the only way used to update the fund tags
 */
@Injectable({ providedIn: 'root' })
export class FundTagStoreService extends FundPreferenceStoreService {
	@InjectConfig(SharedConfigKey.SUPPORTED_FUND_TAGS) private readonly supportedFundTags: FundTagGroupConfig[] = [];

	constructor(@Inject(APP_STORE_CONFIG_TOKEN) protected storeConfig: AppStoreConfig, protected storeService: StoreService) {
		super(storeConfig, storeService);
	}

	public setFundTags(fundTags: FundTagData[]): void {
		this.setPreferenceData(fundTags, FUND_TAGS_STORE);
	}

	public getAllFundTags(): Observable<FundTags> {
		return this.getAll<FundTags, FundTagData>(FUND_TAGS_STORE);
	}

	public getFundTagsById(fundId: string): Observable<FundTagData> {
		return this.getPreferencesById<FundTagData>(fundId, FUND_TAGS_STORE).pipe(
			map((tagsById: StoreEntity<FundTagData>) => tagsById?.[fundId] || ({ tags: [] } as FundTagData))
		);
	}

	protected override getValidFundPreferences<FundTagData>(fundTags: FundTagData[]): FundTagData[] {
		return fundTags.map(fundTagData => {
			// only add fund tags that are in the list of supported tags to the store
			const validTags =
				fundTagData['tags']?.filter(tagId => []
					.concat(...this.supportedFundTags.map((groupConfig: FundTagGroupConfig) => groupConfig.tags))
					.map((tag: FundTagConfig) => tag.id)
					.includes(tagId)
				) ?? [];
			return { ...fundTagData, tags: validTags };
		});
	}
}
